import { gql } from 'graphql-request';

export const USER_FIELDS = gql`
  fragment UserFields on UserDto {
    userId
    firstName
    lastName
    email
    avatar
    lastLogin
    status
    locale
    defaultOrganizationId
    preferences
  }
`;

export const USER_FIELDS_WITH_ROLE = gql`
  fragment UserFieldsWithRole on UserDto {
    userId
    firstName
    lastName
    email
    avatar
    lastLogin
    status
    organizationStatus
    locale
    defaultOrganizationId
    preferences
    role
  }
`;

export const getSelfWithOrganization = gql`
  query getSelfWithOrganization {
    getSelfWithOrganization {
      user {
        ...UserFields
      }
      organization {
        name
        description
        onboardingDetails
        lastProfileUpdate
        organizationId
      }
      role
      organizations {
        organizationId
        name
        description
        role
      }
    }
  }
  ${USER_FIELDS}
`;

export const getLastMessages = gql`
  query getLastMessages {
    getLastMessages {
      messageId
      role
      content
      createdAt
    }
  }
`;

export const getUsers = gql`
  query getUsers {
    getUsers {
      ...UserFields
    }
  }
  ${USER_FIELDS}
`;

export const getUserByEmail = gql`
  query getUserByEmail($email: String!, $emailOrganizationId: String!) {
    getUserByEmail(email: $email, emailOrganizationId: $emailOrganizationId) {
      ...UserFields
    }
  }
  ${USER_FIELDS}
`;

export const getPaginatedUsers = gql`
  query getPaginatedUsers($users: GetPaginatedUsersInput!) {
    getPaginatedUsers(users: $users) {
      users {
        ...UserFields
        failedLoginAttempts
        seatAssignmentId
        seatType
        hasSeat
      }
      count
    }
  }
  ${USER_FIELDS}
`;

export const getPaginatedUsersWithRole = gql`
  query getPaginatedUsers($users: GetPaginatedUsersInput!) {
    getPaginatedUsers(users: $users) {
      users {
        ...UserFieldsWithRole
        seatAssignmentId
        seatType
        hasSeat
      }
        count
    }
  }
  ${USER_FIELDS_WITH_ROLE}
`;

export const getOwnerFromOrganization = gql`
  query getOwnerFromOrganization {
    getOwnerFromOrganization {
      user {
        ...UserFieldsWithRole
      }
    }
  }
  ${USER_FIELDS_WITH_ROLE}
`;

export const getOrganizationUsersEmail = gql`
  query getOrganizationUsersEmail {
    getOrganizationUsersEmail {
      firstName
      lastName
      email
    }
  }
`;

export const getAssignableUsers = gql`
  query getAssignableUsers {
    getAssignableUsers {
      userId
      firstName
      lastName
      email
      role
    }
  }
`;

export const createUser = gql`
  mutation createUser($user: UserCreateInput!) {
    createUser(user: $user) {
      ...UserFields
    }
  }
  ${USER_FIELDS}
`;

export const updateOwnUser = gql`
  mutation updateOwnUser($user: UserUpdateInput!) {
    updateOwnUser(user: $user) {
      ...UserFields
    }
  }
  ${USER_FIELDS}
`;

export const updateOtherUser = gql`
  mutation updateOtherUser($user: UserUpdateInput!) {
    updateOtherUser(user: $user) {
      ...UserFields
    }
  }
  ${USER_FIELDS}
`;

export const disableUser = gql`
  mutation disableUser($id: String!, $organizationId: String) {
    disableUser(id: $id, organizationId: $organizationId) {
      ...UserFields
    }
  }
  ${USER_FIELDS}
`;

export const enableUser = gql`
  mutation enableUser($id: String!, $organizationId: String) {
    enableUser(id: $id, organizationId: $organizationId) {
      ...UserFields
    }
  }
  ${USER_FIELDS}
`;

export const updateDefaultOrganization = gql`
  mutation updateDefaultOrganization($input: UserUpdateDefaultOrganizationInput!) {
    updateDefaultOrganization(input: $input) {
      ...UserFields
    }
  }
  ${USER_FIELDS}
`;

export const completeUserProfile = gql`
  mutation completeUserProfile($profile: UserProfileCompleteInput!) {
    completeUserProfile(profile: $profile) {
      ...UserFields
    }
  }
  ${USER_FIELDS}
`;

export const createUserByEmail = gql`
  mutation createUserByEmail($user: UserCreateByEmailInput!) {
    createUserByEmail(user: $user) {
      ...UserFields
    }
  }
  ${USER_FIELDS}
`;

export const updateUserPreferences = gql`
  mutation updateUserPreferences($input: UserUpdatePreferencesInput!) {
    updateUserPreferences(input: $input) {
      ...UserFields
    }
  }
  ${USER_FIELDS}
`;

const UserGraphQL = {
  queries: {
    getSelfWithOrganization,
    getUsers,
    getUserByEmail,
    getPaginatedUsers,
    getPaginatedUsersWithRole,
    getLastMessages,
    getOwnerFromOrganization,
    getOrganizationUsersEmail,
    getAssignableUsers,
  },
  mutations: {
    createUser,
    createUserByEmail,
    updateOwnUser,
    updateOtherUser,
    disableUser,
    enableUser,
    updateDefaultOrganization,
    completeUserProfile,
    updateUserPreferences,
  },
};

export default UserGraphQL;
